import { ReactElement, useMemo } from "react";
import { ScopeSection } from "../../ScopeSection";
import { localize } from "client_legacy/ndaw/culture";
import { Anchor, Icon } from "client/Components";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { generateTrackingId } from "client/Misc/utils";
import { SearchListItem } from "../../SearchListItem";
import { GlobalSearchTermModel } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import styles from "./RecentSearchListView.module.less";
import { AdvancedSearchItemLayoutAction, ItemLayout } from "../../Items/ItemLayout";
import { Key } from "client/Misc/commonTypes";
import { useKeyboardListNavigationItem } from "client/Hooks";

export type RecentSearchListViewProps = {
	recentSearchQueries: readonly GlobalSearchTermModel[];
	navigationIndexStart: number;
	onSearch(searchTerm: string, version: Guid): void;
	onDelete: (ids: readonly number[]) => void;
};

export const RecentSearchListView = (props: RecentSearchListViewProps): ReactElement => {
	const {
		recentSearchQueries,
		navigationIndexStart,
		onSearch,
		onDelete
	} = props;

	return (
		<ScopeSection
			title={localize("_RecentSearches")}
			trackingId="recent-search"
			titleAction={(
				<Anchor
					trackingId="clear-all"
					onClick={() => onDelete(recentSearchQueries.map(x => x.id))}
				>
					{localize("_ClearAll")}
				</Anchor>
			)}
		>
			<ul className={styles.list}>
				{recentSearchQueries.map((item, index) => (
					<li key={item.id}>
						<SearchListItem
							onExecute={() => onSearch(item.searchTerm, newGuid())}
							trackingId={generateTrackingId("item", `|${item.id}`)}
						>
							<Item
								navigationIndex={navigationIndexStart + index}
								data={item}
								onDelete={onDelete}
								onExecute={() => onSearch(item.searchTerm, newGuid())}
							/>
						</SearchListItem>
					</li>
				))}
			</ul>
		</ScopeSection>
	);
};
RecentSearchListView.displayName = "RecentSearchListView";

type ItemProps = {
	data: GlobalSearchTermModel;
	onDelete: (ids: readonly number[]) => void;
	navigationIndex: number;
	onExecute: () => void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		data,
		onDelete,
		navigationIndex,
		onExecute
	} = props;

	const {
		id,
		searchTerm
	} = data;

	const actions = useMemo(
		(): readonly AdvancedSearchItemLayoutAction[] => ([
			{
				icon: "xmark",
				id: "delete",
				onClick: () => onDelete([id])
			}
		]),
		[
			id,
			onDelete
		]
	);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			trackingId="recent-search-item"
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<Icon
					value={{
						kind: "font",
						name: "clock",
						color: "secondary"
					}}
				/>
			</ItemLayout.IconContainer>
			<ItemLayout.InfoWrapper verticalAlignment="center">
				<ItemLayout.Title trackingId="title">
					{searchTerm}
				</ItemLayout.Title>
			</ItemLayout.InfoWrapper>
			<ItemLayout.Actions
				actions={actions}
			/>
		</ItemLayout>
	);
};
